// src/prototype/components/hub/SectionHeaderBand.tsx
// ============================================================
// SECTION HEADER BAND
//
// The colored header across the top of a hub card: a light Adaptive Navy tint, an icon chip, a
// 13px uppercase title, an optional muted subtitle, and a right slot for meta (chips, small
// stats, buttons). Flush with the card's edges: render it as the FIRST child of a card that has
// `overflow-hidden` (or let it round its own top corners, as it does here) and give the body
// below its own padding.
//
// Palette: Adaptive Navy (#18365E) at 12% for the band and 18% for the icon chip; #4b5563 for the
// subtitle (the muted #717182 is only about 3.9:1 on the tint, below AA at 12px). No other colors.
// ============================================================

import React from 'react';

export interface SectionHeaderBandProps {
  /** Leading icon, typically a lucide icon at w-3.5 h-3.5. */
  icon: React.ReactNode;
  title: string;
  /** Optional one or two lines under the title. */
  subtitle?: React.ReactNode;
  /** Meta or actions, right-aligned and vertically centered. */
  right?: React.ReactNode;
  className?: string;
}

export const SectionHeaderBand: React.FC<SectionHeaderBandProps> = ({ icon, title, subtitle, right, className = '' }) => (
  <div
    className={`flex items-center gap-3 px-6 py-3.5 rounded-t-xl border-b border-[#18365E]/20 bg-[#18365E]/[0.12] ${className}`}
    data-testid="section-header-band"
  >
    <span className="w-7 h-7 rounded-lg bg-[#18365E]/[0.18] text-[#18365E] flex items-center justify-center shrink-0">
      {icon}
    </span>
    <div className="min-w-0 flex-1">
      <h3 className="text-[13px] font-semibold uppercase tracking-wide text-[#18365E] leading-tight">{title}</h3>
      {subtitle && <p className="text-xs text-[#4b5563] leading-snug mt-0.5">{subtitle}</p>}
    </div>
    {right && <div className="flex items-center gap-3 flex-wrap justify-end shrink-0 max-w-[60%]">{right}</div>}
  </div>
);

export default SectionHeaderBand;
